// Contexto para gerenciar empresa selecionada
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getEmpresasUsuario } from '../services/usuariosService';
import { getEmpresaById } from '../services/empresasService';
import { supabase } from '../lib/supabase';
import type { Empresa } from '../types';

interface EmpresaContextType {
  empresaSelecionada: Empresa | null;
  contaId: string | null;
  empresas: Empresa[];
  loading: boolean;
  isFirstTime: boolean;
  setEmpresaSelecionada: (empresa: Empresa | null) => void;
  carregarEmpresas: () => Promise<void>;
  atualizarEmpresaSelecionada: () => Promise<void>;
  adicionarEmpresa: (empresa: Empresa) => void; // Adicionar empresa diretamente sem buscar do banco
  setIsFirstTime: (value: boolean) => void;
}

const EmpresaContext = createContext<EmpresaContextType | undefined>(undefined);

const EMPRESA_SELECIONADA_KEY = 'finance_a1_empresa_selecionada';

export function EmpresaProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [empresaSelecionada, setEmpresaSelecionadaState] = useState<Empresa | null>(null);
  const [contaId, setContaId] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false); // Começar como false, será true apenas quando carregar
  const [isFirstTime, setIsFirstTimeState] = useState(false);

  // Carregar empresas do usuário - VERSÃO SIMPLIFICADA
  const carregarEmpresas = async () => {
    // Se não está autenticado ou ainda está carregando auth, não fazer nada
    if (authLoading || !isAuthenticated) {
      setLoading(false);
      carregandoRef.current = false;
      return;
    }

    // Se já está carregando, não fazer nada (evitar múltiplas chamadas)
    if (carregandoRef.current) {
      return;
    }

    try {
      carregandoRef.current = true;
      setLoading(true);
      
      // Buscar vínculos
      const vinculos = await getEmpresasUsuario();
      
      if (vinculos.length === 0) {
        // Sem empresas - parar loading imediatamente
        setEmpresas([]);
        setLoading(false);
        carregandoRef.current = false;
        return;
      }
      
      // Buscar detalhes das empresas
      const empresasPromises = vinculos.map(async (vinculo) => {
        const empresa = await getEmpresaById(vinculo.empresaId);
        if (empresa) {
          // Buscar conta_id da empresa diretamente do banco
          try {
            const { data: empresaData, error } = await (supabase as any)
              .from('empresas')
              .select('conta_id')
              .eq('id', empresa.id)
              .single();
            
            if (!error && empresaData?.conta_id) {
              // Adicionar conta_id ao objeto Empresa
              (empresa as any).contaId = empresaData.conta_id;
            }
          } catch (err) {
            console.error('[EmpresaContext] Erro ao buscar conta_id:', err);
          }
        }
        return empresa;
      });
      const empresasData = await Promise.all(empresasPromises);
      const empresasValidas = empresasData.filter((e): e is Empresa => e !== null);
      
      setEmpresas(empresasValidas);
      
      // Verificar se é primeiro uso
      const firstTimeCompleted = localStorage.getItem('nfsis_first_time_completed');
      if (empresasValidas.length === 0 && !firstTimeCompleted) {
        setIsFirstTimeState(true);
      } else {
        setIsFirstTimeState(false);
      }
      
      // Se houver empresa selecionada, atualizar conta_id
      if (empresaSelecionada && empresasValidas.length > 0) {
        const empresaAtualizada = empresasValidas.find(e => e.id === empresaSelecionada.id);
        if (empresaAtualizada && (empresaAtualizada as any).contaId) {
          setContaId((empresaAtualizada as any).contaId);
        }
      }

      // Selecionar empresa automaticamente
      if (empresasValidas.length > 0) {
        let empresaParaSelecionar: Empresa | null = null;
        
        // Verificar localStorage primeiro
        const empresaIdSalva = localStorage.getItem(EMPRESA_SELECIONADA_KEY);
        if (empresaIdSalva) {
          empresaParaSelecionar = empresasValidas.find(e => e.id === empresaIdSalva) || null;
        }
        
        // Se não encontrou, usar a primeira (sempre selecionar automaticamente)
        if (!empresaParaSelecionar) {
          empresaParaSelecionar = empresasValidas[0];
        }
        
        // SEMPRE selecionar empresa se houver (garantir que não fique null quando há empresas)
        if (empresaParaSelecionar) {
          console.log('[EmpresaContext] Selecionando empresa automaticamente:', empresaParaSelecionar.nomeFantasia);
          setEmpresaSelecionadaState(empresaParaSelecionar);
          localStorage.setItem(EMPRESA_SELECIONADA_KEY, empresaParaSelecionar.id);
          
          // Se houver conta_id na empresa, atualizar
          if ('contaId' in empresaParaSelecionar && (empresaParaSelecionar as any).contaId) {
            setContaId((empresaParaSelecionar as any).contaId);
          } else {
            // Buscar conta_id se não estiver no objeto
            try {
              const { data: empresaData, error } = await (supabase as any)
                .from('empresas')
                .select('conta_id')
                .eq('id', empresaParaSelecionar.id)
                .single();
              
              if (!error && empresaData?.conta_id) {
                setContaId(empresaData.conta_id);
                (empresaParaSelecionar as any).contaId = empresaData.conta_id;
              }
            } catch (err) {
              console.error('[EmpresaContext] Erro ao buscar conta_id:', err);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('[EmpresaContext] Erro ao carregar empresas:', error);
      setEmpresas([]);
    } finally {
      // SEMPRE garantir que loading seja false e flag seja resetada
      setLoading(false);
      carregandoRef.current = false;
    }
  };

  // Atualizar empresa selecionada
  const atualizarEmpresaSelecionada = async () => {
    if (empresaSelecionada) {
      const empresaAtualizada = await getEmpresaById(empresaSelecionada.id);
      if (empresaAtualizada) {
        setEmpresaSelecionadaState(empresaAtualizada);
      }
    }
  };

  // Função para adicionar empresa diretamente ao array (sem buscar do banco)
  const adicionarEmpresa = (empresa: Empresa) => {
    setEmpresas(prev => {
      // Verificar se já existe para evitar duplicatas
      if (prev.find(e => e.id === empresa.id)) {
        return prev;
      }
      return [...prev, empresa];
    });
  };

  // Função para definir empresa selecionada
  const setEmpresaSelecionada = async (empresa: Empresa | null) => {
    setEmpresaSelecionadaState(empresa);
    if (empresa) {
      localStorage.setItem(EMPRESA_SELECIONADA_KEY, empresa.id);
      // Adicionar empresa ao array se não existir (para evitar carregarEmpresas)
      setEmpresas(prev => {
        if (prev.find(e => e.id === empresa.id)) {
          return prev;
        }
        return [...prev, empresa];
      });
      // Não buscar conta_id aqui para evitar recursão nas políticas RLS
    } else {
      localStorage.removeItem(EMPRESA_SELECIONADA_KEY);
      setContaId(null);
    }
  };

  // Flag para controlar se já tentou carregar empresas (evitar loops)
  const tentouCarregarRef = useRef(false);
  const carregandoRef = useRef(false); // Evitar múltiplas chamadas simultâneas
  
  // Carregar empresa do localStorage quando o componente montar (ANTES de carregar do banco)
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    
    // Tentar carregar empresa salva do localStorage primeiro
    const empresaIdSalva = localStorage.getItem(EMPRESA_SELECIONADA_KEY);
    if (empresaIdSalva && !empresaSelecionada) {
      // Tentar buscar a empresa do array se já estiver carregada
      const empresaEncontrada = empresas.find(e => e.id === empresaIdSalva);
      if (empresaEncontrada) {
        setEmpresaSelecionadaState(empresaEncontrada);
      }
    }
  }, [isAuthenticated, authLoading]);
  
  // Carregar empresas quando o usuário estiver autenticado - VERSÃO SIMPLIFICADA
  useEffect(() => {
    // Aguardar autenticação terminar
    if (authLoading) {
      setLoading(false);
      return;
    }

    // Se não autenticado, limpar e resetar flags
    if (!isAuthenticated) {
      setLoading(false);
      setEmpresas([]);
      setEmpresaSelecionadaState(null);
      setContaId(null);
      tentouCarregarRef.current = false;
      carregandoRef.current = false;
      localStorage.removeItem(EMPRESA_SELECIONADA_KEY);
      return;
    }

    // Se autenticado e ainda não tentou carregar E não está carregando agora
    if (isAuthenticated && !tentouCarregarRef.current && !carregandoRef.current) {
      tentouCarregarRef.current = true;
      
      carregarEmpresas().finally(() => {
        // Resetar flag após carregar (sucesso ou erro)
        carregandoRef.current = false;
        // NÃO resetar tentouCarregarRef aqui - queremos garantir que só tenta uma vez
      });
    }
  }, [authLoading, isAuthenticated]); // Apenas estas dependências para evitar loops
  
  // Timeout de segurança separado para não causar loops
  useEffect(() => {
    if (isAuthenticated && loading && empresas.length === 0) {
      const timeout = setTimeout(() => {
        console.warn('[EmpresaContext] Timeout de segurança: parando loading após 3 segundos');
        setLoading(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, loading, empresas.length]);

  const setIsFirstTime = (value: boolean) => {
    setIsFirstTimeState(value);
    if (value) {
      localStorage.removeItem('nfsis_first_time_completed');
    } else {
      localStorage.setItem('nfsis_first_time_completed', 'true');
    }
  };

  const value: EmpresaContextType = {
    empresaSelecionada,
    contaId,
    empresas,
    loading,
    isFirstTime,
    setEmpresaSelecionada,
    carregarEmpresas,
    atualizarEmpresaSelecionada,
    adicionarEmpresa,
    setIsFirstTime,
  };

  return <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>;
}

export function useEmpresa() {
  const context = useContext(EmpresaContext);
  if (context === undefined) {
    throw new Error('useEmpresa deve ser usado dentro de um EmpresaProvider');
  }
  return context;
}

